// Loads data for details page
import {
  CategoryActionTypes,
  CategoryName,
  RootActionTypes,
  RootState,
  SharedCategoryState,
  Tab
} from "./userGroupDetailsTypes";
import { CategoryDiv, CategorySwitcher } from "elements";
import React from "react";
import { Layout } from "views/Main/Layouts";
import { LocalKeys, useTranslation } from "utilities/i18n";
import { UserGroup } from "generated/graphql.schema";
import { ProtectedLink } from "TsRoleGuards";
import { DeviceUsersTable } from "routes/Manage/DeviceUsers/Shared/DeviceUsers/DeviceUsersTable";
import { DeviceUserInfo } from "routes/Manage/DeviceUsers/Shared/utils/deviceUserUtils";
import { useDeviceUserCount } from "routes/Manage/DeviceUsers/Shared/utils/useDeviceUserCount";
import {
  RemoveUsersModal,
  AddUsersModal
} from "routes/Manage/DeviceUsers/Shared/Modals";
import { needs, useGuard } from "rbac";

export const UserGroupDetailsView: React.FC<{
  rootState: RootState;
  rootStateUpdate: RootActionTypes;
  groupedState: SharedCategoryState;
  groupedUpdate: CategoryActionTypes;
  availableState: SharedCategoryState;
  availableUpdate: CategoryActionTypes;
  availablePageInfo?: {
    hasNextPage: boolean;
    endCursor: string;
  };
  groupData: Omit<UserGroup, "users" | "policies"> | undefined;
  availableUsers: Array<DeviceUserInfo>;
  groupedUsers: Array<DeviceUserInfo>;
  tenantId: string;
}> = ({
  rootState,
  rootStateUpdate,
  availableState,
  availableUpdate,
  availablePageInfo,
  groupedState,
  groupedUpdate,
  groupData,
  availableUsers,
  groupedUsers,
  tenantId
}) => {
  const { tab } = rootState;

  const { t } = useTranslation();
  const { deviceUserCount } = useDeviceUserCount(tenantId);
  const userCanEdit = useGuard(needs("update:usergroup"));

  const switchTab = (tab: Tab) => {
    rootStateUpdate.updateTab(tab);
  };

  const inThisGroupCount = groupedState.pageLoading
    ? "?"
    : `${groupedUsers.length}`;
  const availableToAddCount =
    groupedState.pageLoading ||
    availableState.pageLoading ||
    deviceUserCount == null
      ? "?"
      : `${deviceUserCount - groupedUsers?.length}`;

  const { Content } = Layout;
  return (
    <Content className="device-group-details device-inventory">
      <DeviceUserGroupTitle groupName={groupData?.name ?? ""} page="OVERVIEW" />
      <section>
        <CategorySwitcher>
          <CategoryDiv
            data-testid="grouped-tab"
            label={t("IN_THIS_GROUP", { inThisGroupCount })}
            active={tab === Tab.GROUPED_USERS}
            onClick={() => switchTab(Tab.GROUPED_USERS)}
          />
          <CategoryDiv
            data-testid="available-tab"
            label={t("AVAILABLE_TO_ADD", { availableToAddCount })}
            active={tab === Tab.AVAILABLE_TO_ADD}
            onClick={() => switchTab(Tab.AVAILABLE_TO_ADD)}
          />
        </CategorySwitcher>
      </section>
      {tab === Tab.GROUPED_USERS && (
        <DeviceUsersTable
          actions={[
            props => (
              <ProtectedLink
                nest
                to={{ pathname: "/remove" }}
                className={[
                  "btn",
                  "btn-primary",
                  props.disabled ? "btn-disabled" : undefined
                ]
                  .filter(c => !!c)
                  .join(" ")}
                requirement="update:usergroup"
                disabled={props.disabled}
              >
                {t("REMOVE_FROM_GROUP")}
              </ProtectedLink>
            )
          ]}
          routes={
            groupData
              ? [
                  {
                    nestedRoute: "/remove",
                    requirement: "grant:role",
                    refresh: () => {},
                    Modal: ({ deviceUsers }) => (
                      <RemoveUsersModal
                        tenantId={tenantId}
                        groupData={groupData}
                        showLinkOnSuccess={false}
                        deviceUsers={deviceUsers}
                        urlEndPath="/remove"
                      />
                    )
                  }
                ]
              : []
          }
          loading={groupedState.pageLoading}
          userCanEdit={userCanEdit}
          deviceUserList={groupedUsers ?? []}
          selectedItems={groupedState.selectedItems}
          setSelectedItems={(items: string[]) => {
            groupedUpdate.updateSelectedItems(
              CategoryName.GROUPED_USERS,
              items
            );
          }}
          hasDeviceUsers={(groupedUsers ?? []).length > 0}
          noUsersMessage={"NO_USERS_IN_USER_GROUP_MESSAGE"}
        />
      )}
      {tab === Tab.AVAILABLE_TO_ADD && (
        <DeviceUsersTable
          actions={[
            props => (
              <ProtectedLink
                nest
                to={{ pathname: "/add" }}
                className={[
                  "btn",
                  "btn-primary",
                  props.disabled ? "btn-disabled" : undefined
                ]
                  .filter(c => !!c)
                  .join(" ")}
                requirement="update:usergroup"
                disabled={props.disabled}
              >
                {t("ADD_TO_GROUP")}
              </ProtectedLink>
            )
          ]}
          routes={
            groupData
              ? [
                  {
                    nestedRoute: "/add",
                    requirement: "grant:role",
                    refresh: () => {},
                    Modal: ({ deviceUsers }) => (
                      <AddUsersModal
                        tenantId={tenantId}
                        groupData={groupData}
                        showLinkOnSuccess={false}
                        deviceUsers={deviceUsers}
                        urlEndPath="/add"
                      />
                    )
                  }
                ]
              : []
          }
          loading={availableState.pageLoading}
          userCanEdit={userCanEdit}
          setUsersSearchTerm={(term: string | undefined) => {
            availableUpdate.updateSearchTerm(
              CategoryName.AVAILABLE_TO_ADD,
              term
            );
          }}
          hasNextPage={availablePageInfo?.hasNextPage}
          endCursor={availablePageInfo?.endCursor}
          deviceUserList={availableUsers}
          selectedItems={availableState.selectedItems}
          setSelectedItems={(items: string[]) => {
            availableUpdate.updateSelectedItems(
              CategoryName.AVAILABLE_TO_ADD,
              items
            );
          }}
          hasDeviceUsers={
            availableUsers.filter(
              user => !groupedUsers.find(gu => gu.user_id === user.user_id)
            ).length > 0 ||
            (availableState.searchTerm != null &&
              availableState.searchTerm.length !== 0)
          }
          hiddenItems={groupedUsers.map(user => user.user_id)}
          updateCursor={(cursor: string) => {
            availableUpdate.updateCursor(CategoryName.AVAILABLE_TO_ADD, cursor);
          }}
          noUsersMessage={"NO_USERS_AVAILABLE_TO_ADD_MESSAGE"}
        />
      )}
    </Content>
  );
};

const DeviceUserGroupTitle: React.FC<{
  groupName: string;
  page: LocalKeys;
}> = ({ groupName, page }) => {
  const { t } = useTranslation();

  const { Content } = Layout;
  return (
    <Content.Title>
      {`${t(page)} `}
      <p>{`| ${groupName}`}</p>
    </Content.Title>
  );
};
